import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCompletePartnerTaskMutation,
  useGetPartnerTaskRewardMutation,
} from '@api/queries/user';
import { type PartnerTaskDto } from '@common/types/query/tasks';
import { StringMain } from '@components/Typography/Typography';
import { Container } from '@components/ui/Container/container';
import { ValueTag } from '@components/ui/ValueTag/ValueTag';
import { useAppDispatch } from '@redux/store';
import cn from 'classnames';

import { defaultToastConfig } from '@/common/config/toast';
import { type CurrencyType } from '@/common/types/user';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { addToBalance } from '@/redux/reducers/userReducer';

import ErrorMessageBlock from '../ErrorMessageBlock/ErrorMessageBlock';
import TaskButton from '../TaskButton/TaskButton';

const SocialTaskItem: FC<PartnerTaskDto> = ({
  id,
  title,
  link,
  reward,
  icon,
  status,
  delayTimer,
}) => {
  const dispatch = useAppDispatch();
  const [completeTask] = useCompletePartnerTaskMutation();
  const [claimTaskReward] = useGetPartnerTaskRewardMutation();
  const [waitResponse, setWaitResponse] = useState(false);
  const { t } = useTranslation();

  const handleClick = async (): Promise<void> => {
    if (status === 'complete' && delayTimer === 0) {
      await onClaimReward();

      return;
    }

    if (status !== 'paid' && delayTimer === 0) {
      await completeTask({ taskId: id });
    }

    if (link.includes('t.me')) {
      Telegram.WebApp.openTelegramLink(link);
    } else {
      Telegram.WebApp.openLink(link);
    }
  };

  const onClaimReward = async (): Promise<void> => {
    try {
      setWaitResponse(true);
      await claimTaskReward({ taskId: id })
        .unwrap()
        .then((data) => {
          if (data.status === 'paid') {
            dispatch(addToBalance(reward));
            showToast(
              t('The reward has been successfully received!'),
              { img: icon ? icon : '/img/navigation/tasks.png' },
              {
                ...defaultToastConfig,
                className: 'justify-center',
              },
            );
          } else {
            showToast(
              `${t('Task requirements not met.')} ${title}`,
              { img: icon ? icon : '/img/navigation/tasks.png' },
              {
                ...defaultToastConfig,
                className: 'justify-center',
              },
            );
          }
        });
    } catch (error: unknown) {
      console.error(error);
      showToast(t('The task is not yet completed!'), undefined, {
        ...defaultToastConfig,
        className: 'justify-center',
      });
    } finally {
      setWaitResponse(false);
    }
  };
  const createIcon = icon ? icon : '/img/navigation/tasks.png';

  return (
    <div key={id}>
      {status === 'pending' && delayTimer === 0 && <ErrorMessageBlock />}
      <Container
        variant="task"
        padding="16"
        onClick={handleClick}
        className={cn(
          'flex items-center gap-4 w-full h-[4.5rem] cursor-pointer relative overflow-hidden',
          status === 'pending' &&
            delayTimer === 0 &&
            '!rounded-tl-[0] !rounded-tr-[0]',
        )}
      >
        {status === 'complete' && delayTimer === 0 && (
          <>
            <div className="active-green-gradient left-[30%]"></div>
            <div className="active-green-point !top-[5%] !right-[5px]"></div>
          </>
        )}
        {/* <SocialIcon name={socialNetwork} /> */}
        <img src={createIcon} alt="icon" width={40} />
        <div className="flex flex-col gap-2 flex-1">
          <StringMain weight="medium">{title}</StringMain>
          <div className="flex gap-2 items-center">
            {Object.entries(reward).map(([key, value]) => (
              <ValueTag
                variant="reward"
                value={value}
                kind={key as CurrencyType}
              />
            ))}
          </div>
        </div>
        <div className="w-[5.25rem] h-[2rem] absolute top-1/2 right-4 transform -translate-y-1/2 flex items-center justify-end">
          {status === 'paid' ? (
            <svg id="icon-checkmark" viewBox="0 0 32 32" width={20}>
              <path
                style={{ fill: '#82e024' }}
                d="M27 4l-15 15-7-7-5 5 12 12 20-20z"
              ></path>
            </svg>
          ) : (
            <TaskButton
              isWaitForClaim={delayTimer > 0}
              waitResponse={waitResponse}
              timer={delayTimer}
              isClaimButtonActive={status === 'complete' && delayTimer === 0}
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export { SocialTaskItem };
